// Axial-coordinate hex math. Pointy-top orientation.
// Spec: docs/mechanics.md §1.
export const DIRECTIONS = ['E', 'NE', 'NW', 'W', 'SW', 'SE'];
const OFFSETS = {
    E: { q: 1, r: 0 },
    NE: { q: 1, r: -1 },
    NW: { q: 0, r: -1 },
    W: { q: -1, r: 0 },
    SW: { q: -1, r: 1 },
    SE: { q: 0, r: 1 },
};
export function coord(q, r) {
    return { q, r };
}
export function coordKey(c) {
    return `${c.q},${c.r}`;
}
export function coordEquals(a, b) {
    return a.q === b.q && a.r === b.r;
}
export function offset(dir) {
    return OFFSETS[dir];
}
export function step(c, dir) {
    const d = OFFSETS[dir];
    return { q: c.q + d.q, r: c.r + d.r };
}
export function neighbors(c) {
    return DIRECTIONS.map((d) => step(c, d));
}
// Hex distance in axial coordinates.
export function distance(a, b) {
    const dq = a.q - b.q;
    const dr = a.r - b.r;
    const ds = -dq - dr;
    return (Math.abs(dq) + Math.abs(dr) + Math.abs(ds)) / 2;
}
// Rounded mean (centroid) of a set of cells, biased to a tie-break cell.
export function centroid(cells, tiebreak) {
    if (cells.length === 0)
        throw new Error('centroid of empty set');
    let sq = 0, sr = 0;
    for (const c of cells) {
        sq += c.q;
        sr += c.r;
    }
    const mq = sq / cells.length;
    const mr = sr / cells.length;
    // Round to nearest integer axial cell using cube-round.
    return cubeRound(mq, mr, tiebreak);
}
function cubeRound(fq, fr, tiebreak) {
    const fs = -fq - fr;
    let rq = Math.round(fq);
    let rr = Math.round(fr);
    let rs = Math.round(fs);
    const dq = Math.abs(rq - fq);
    const dr = Math.abs(rr - fr);
    const ds = Math.abs(rs - fs);
    if (dq > dr && dq > ds)
        rq = -rr - rs;
    else if (dr > ds)
        rr = -rq - rs;
    // (don't need rs after)
    const rounded = { q: rq, r: rr };
    // If a tiebreak cell is provided and it's one of the plausible nearest cells
    // (distance 1 from rounded), prefer it when the fractional residual is borderline.
    if (tiebreak && distance(rounded, tiebreak) <= 1) {
        const tiebreakFrac = Math.abs(tiebreak.q - fq) + Math.abs(tiebreak.r - fr);
        const roundedFrac = Math.abs(rq - fq) + Math.abs(rr - fr);
        if (Math.abs(tiebreakFrac - roundedFrac) < 1e-9)
            return tiebreak;
    }
    return rounded;
}
// Enumerate all cells inside a hex-of-hexes region of the given radius.
// Count = 1 + 3 * R * (R + 1).
export function hexRegion(radius) {
    const out = [];
    for (let q = -radius; q <= radius; q++) {
        const rMin = Math.max(-radius, -q - radius);
        const rMax = Math.min(radius, -q + radius);
        for (let r = rMin; r <= rMax; r++) {
            out.push({ q, r });
        }
    }
    return out;
}
// Lexicographic comparison for deterministic tie-breaks.
export function compareCoord(a, b) {
    if (a.q !== b.q)
        return a.q - b.q;
    return a.r - b.r;
}
